import React from 'react';
import { ICONS } from '../../icons';
import type { IconColor, IconName, IconSize } from '../../icons/types';
import { ICON_COLORS, isIconName } from '../../icons/types';

/**
 * Icon — v2 atom.
 *
 * Renderiza un icono SVG del registro (`src/ui/icons/`). El registro se
 * autogenera con `npm run icons:gen`.
 *
 * Accesibilidad:
 *  - Si se pasa `title`, el icono es informativo: añade `role="img"` y
 *    `aria-label={title}` con `aria-hidden="false"`.
 *  - Si no, es decorativo: `aria-hidden="true"` y sin `role`.
 *
 * Coloreado:
 *  - Mecanismo CSS-only basado en `currentColor` (ver `_icon.scss`).
 *  - Si el icono debe preservar sus fills nativos (gradiente o logo
 *    multi-color, `preservesFills: true` en el registro) se añade
 *    automáticamente `.icon--color-gradient` para inhibir el
 *    reemplazo de fills.
 */

export type IconProps = {
  name: IconName;
  size?: IconSize;
  color?: IconColor;
  title?: string;
  ariaHidden?: boolean;
  className?: string;
};

function Icon({ ariaHidden = true, className, color = 'default', name, size = 'md', title }: IconProps): JSX.Element | null {
  if (!isIconName(name)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Unknown icon name: "${name}". Render skipped.`);
    }
    return null;
  }

  // JS call-sites bypass the IconColor type. Warn (dev only) when an unknown
  // colour value lands here so the icon does not silently fall back to the
  // parent's `currentColor`.
  if (process.env.NODE_ENV !== 'production' && !(ICON_COLORS as readonly string[]).includes(color)) {
    // eslint-disable-next-line no-console
    console.warn(`[Icon] Unknown color: "${color}" on icon "${name}". Allowed: ${ICON_COLORS.join(', ')}.`);
  }

  const entry = ICONS[name];
  const SvgComponent = entry.Component;
  const isInformative = typeof title === 'string' && title.length > 0;
  const effectiveAriaHidden = isInformative ? false : ariaHidden;

  // Precedencia: si el icono preserva sus fills nativos (gradiente o logo
  // multi-color), omitimos la clase de color para que `currentColor` no
  // contamine los fills hardcoded. La clase `.icon--color-gradient` es la
  // única relacionada con color que se aplica en ese caso.
  const colorClass = entry.preservesFills ? 'icon--color-gradient' : `icon--color-${color}`;

  const classes = ['icon', `icon--size-${size}`, colorClass, className].filter(Boolean).join(' ');

  return (
    <span className="icon__wrapper">
      <span aria-hidden={effectiveAriaHidden} aria-label={isInformative ? title : undefined} className={classes} role={isInformative ? 'img' : undefined}>
        <SvgComponent focusable={false} />
      </span>
    </span>
  );
}

export default Icon;
